"use client";

import { Button } from "@ethera/ui/button";
import { SOCIAL_LINKS } from "@guru/shared/constants/social-links";
import { useTranslation } from "@guru/shared/i18n";
import { MessageCircle, ShieldCheck } from "lucide-react";
import { LedgerSettleScene } from "@/components/marketing/ledger-settle-scene";
import { TrialCTA } from "@/components/marketing/trial-cta";
import { ScrollReveal } from "@/components/public/scroll-reveal";

export function FinalCTASection() {
  const { t } = useTranslation();

  // Find WhatsApp link from social links
  const whatsappLink = SOCIAL_LINKS.find((s) => s.name === "WhatsApp")?.href;

  return (
    <section id="cta" className="relative overflow-clip bg-secondary/30 py-20 sm:py-24">
      {/* Ledger-grid backdrop */}
      <div
        className="absolute inset-0 ledger-grid ledger-grid-fade pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card/70 shadow-xl backdrop-blur-sm">
          {/* Soft saffron glow anchoring the panel */}
          <div
            className="pointer-events-none absolute -left-16 -top-16 h-56 w-56 rounded-full bg-brand/15 blur-3xl"
            aria-hidden="true"
          />

          <div className="relative grid items-center gap-10 p-8 sm:p-10 lg:grid-cols-2 lg:gap-12 lg:p-14">
            {/* Copy + CTA */}
            <ScrollReveal threshold={0.3} className="min-w-0">
              <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-deep">
                Your move
              </p>
              <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {t.homepage.finalCta.headline}
              </h2>
              <p className="mt-4 max-w-xl text-pretty text-lg text-muted-foreground">
                {t.homepage.finalCta.subheadline}
              </p>

              <div className="mt-8 flex w-full flex-col items-stretch gap-4 sm:w-auto sm:flex-row sm:items-center">
                <TrialCTA
                  placement="final-cta"
                  label={t.homepage.finalCta.cta}
                  className="cta-glow w-full justify-center sm:w-auto"
                />

                {whatsappLink && (
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="h-auto min-h-12 w-full justify-center gap-2 whitespace-normal px-6 py-2 text-center sm:w-auto"
                  >
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{t.homepage.finalCta.whatsappAlternative}</span>
                    </a>
                  </Button>
                )}
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                {t.homepage.finalCta.alternative}
              </p>

              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="size-3.5 text-positive" aria-hidden="true" />
                {t.homepage.finalCta.privacyNote}
              </p>
            </ScrollReveal>

            {/* Live ledger diorama */}
            <ScrollReveal delay={150} threshold={0.3} className="min-w-0">
              <LedgerSettleScene />
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
